// Domain Error Hierarchy - Central export point for all domain errors
// These errors represent business rule violations and domain-specific failures

// Base error interfaces
export type { 
  BaseDomainErrorData, 
  ValidationErrorData, 
  BusinessRuleViolationErrorData, 
  ResourceErrorData 
} from "./DomainError";

// Validation errors
export {
  ValidationError,
  MissingDataError,
  InvalidFormatError,
  OutOfRangeError,
  InvalidLengthError,
  InvalidEnumValueError,
  SchemaValidationError,
} from "./ValidationErrors";

// Business rule violation errors
export {
  BusinessRuleViolationError,
  ImmutabilityViolationError,
  UniquenessViolationError,
  InvalidStateTransitionError,
  OperationNotAllowedError,
  FinancialCalculationError,
  CurrencyMismatchError,
} from "./BusinessErrors";

// Resource errors
export {
  ResourceError,
  ResourceNotFoundError,
  ResourceAccessDeniedError,
  ResourceInUseError,
  ResourceLockedError,
  ResourceStateError,
} from "./ResourceErrors";

// Domain-specific errors
export {
  // Invoice errors
  InvoiceNumberFormatError,
  InvoiceNumberConflictError,
  InvoiceValidationError,
  InvoiceImmutabilityError,
  
  // Order errors
  OrderNotFoundError,
  OrderValidationError,
  OrderStateError,
  
  // PDF errors
  PDFGenerationError,
  PDFComplianceError,
  
  // Storage errors
  StorageError,
  StorageUploadError,
  
  // Repository errors
  RepositoryError,
  DatabaseConstraintError,
} from "./DomainSpecificErrors";

// Error categories for filtering and handling
export const ErrorCategories = {
  VALIDATION: [
    "@/ValidationError",
    "@/MissingDataError",
    "@/InvalidFormatError",
    "@/OutOfRangeError",
    "@/InvalidLengthError",
    "@/InvalidEnumValueError",
    "@/SchemaValidationError",
  ],
  BUSINESS_RULES: [
    "@/BusinessRuleViolationError",
    "@/ImmutabilityViolationError",
    "@/UniquenessViolationError",
    "@/InvalidStateTransitionError",
    "@/OperationNotAllowedError",
    "@/FinancialCalculationError",
    "@/CurrencyMismatchError",
  ],
  RESOURCES: [
    "@/ResourceError",
    "@/ResourceNotFoundError",
    "@/ResourceAccessDeniedError",
    "@/ResourceInUseError",
    "@/ResourceLockedError",
    "@/ResourceStateError",
  ],
  INVOICE: [
    "@/InvoiceNumberFormatError",
    "@/InvoiceNumberConflictError",
    "@/InvoiceValidationError",
    "@/InvoiceImmutabilityError",
  ],
  ORDER: [
    "@/OrderNotFoundError",
    "@/OrderValidationError",
    "@/OrderStateError",
  ],
  PDF: [
    "@/PDFGenerationError",
    "@/PDFComplianceError",
  ],
  STORAGE: [
    "@/StorageError",
    "@/StorageUploadError",
  ],
  REPOSITORY: [
    "@/RepositoryError",
    "@/DatabaseConstraintError",
  ],
} as const;

// Retry strategies by error category
export const RetryStrategies = {
  // Never retry validation or business rule errors - they're deterministic
  NON_RETRYABLE: [
    ...ErrorCategories.VALIDATION,
    ...ErrorCategories.BUSINESS_RULES,
    ...ErrorCategories.INVOICE,
    ...ErrorCategories.ORDER,
  ],
  
  // Retry technical errors with exponential backoff
  RETRYABLE_WITH_BACKOFF: [
    ...ErrorCategories.STORAGE,
    ...ErrorCategories.REPOSITORY,
  ],
  
  // Retry resource errors with linear backoff
  RETRYABLE_LINEAR: [
    ...ErrorCategories.RESOURCES,
  ],
  
  // PDF errors may be retryable depending on the specific error
  CONDITIONAL_RETRY: [
    ...ErrorCategories.PDF,
  ],
} as const;

// Helper functions for error categorization
export const isValidationError = (errorTag: string): boolean =>
  ErrorCategories.VALIDATION.includes(errorTag as any);

export const isBusinessRuleError = (errorTag: string): boolean =>
  ErrorCategories.BUSINESS_RULES.includes(errorTag as any);

export const isResourceError = (errorTag: string): boolean =>
  ErrorCategories.RESOURCES.includes(errorTag as any);

export const isRetryableError = (errorTag: string): boolean =>
  [...RetryStrategies.RETRYABLE_WITH_BACKOFF, ...RetryStrategies.RETRYABLE_LINEAR].includes(errorTag as any);

export const isNonRetryableError = (errorTag: string): boolean =>
  RetryStrategies.NON_RETRYABLE.includes(errorTag as any);
